/// useVariableInspector: React hook for inspecting process instance variables.
/// Sends "inspect" commands to the BPMN engine worker and auto-refreshes
/// when engine events arrive for the selected instance.

import { useState, useEffect, useCallback } from "react";
import { getWorkerManager } from "@/lib/WorkerManager";
import type { InspectInstanceResponse } from "@/protocol";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VariableScope {
  instanceKey: string;
  variables: Record<string, unknown>;
  state: string;
  lastUpdated: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook for inspecting variables of a specific process instance.
 * Fetches variables via the "inspect" command and auto-refreshes on engine
 * events that reference the selected instance.
 */
export function useVariableInspector(instanceKey: string | null) {
  const [scope, setScope] = useState<VariableScope | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch variables via inspect command
  const refresh = useCallback(async () => {
    if (!instanceKey) return;

    setIsLoading(true);
    setError(null);
    try {
      const manager = getWorkerManager();
      const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
      if (!engineWorkerId) {
        setError("No BPMN engine worker is running");
        return;
      }

      const result = await manager.sendCommand(
        engineWorkerId,
        {
          type: "inspect",
          direction: "cmd" as const,
          payload: { instance_key: instanceKey },
        } as never,
        10000,
      );

      const respPayload = result.payload as InspectInstanceResponse["payload"];
      if (respPayload.error) {
        setError(respPayload.error);
        return;
      }

      const variables = JSON.parse(respPayload.variables ?? "{}");
      setScope({
        instanceKey,
        variables,
        state: respPayload.state ?? "unknown",
        lastUpdated: Date.now(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to inspect instance");
    } finally {
      setIsLoading(false);
    }
  }, [instanceKey]);

  // Auto-refresh when engine events arrive for this instance
  useEffect(() => {
    if (!instanceKey) {
      setScope(null);
      return;
    }

    const manager = getWorkerManager();
    const unsub = manager.onEvent((_sourceId, envelope) => {
      // Refresh on any event related to this instance
      const eventPayload = envelope.payload as Record<string, unknown> | undefined;
      const eventInstanceKey = eventPayload?.instance_key;
      if (eventInstanceKey === instanceKey) {
        refresh();
      }
    });

    // Initial fetch
    refresh();

    return unsub;
  }, [instanceKey, refresh]);

  return { scope, isLoading, error, refresh };
}
